import { useUserStore } from '@talon/web/src/context';
import { StorageProperties } from '@talon/web/src/types';
import Storage from '@talon/web/src/utils/storage';
import { Box, Button, Text } from 'grommet';
import * as jwt from 'jsonwebtoken';
import { observer } from 'mobx-react-lite';
import { useMemo } from 'react';
import { User } from '@talon/web/lib/api';
import { isTalentManagerUser } from '../../utils/userRoles';

/**
 * Shown at the top of every shell (Layout, AdminLayout) whenever the current session was
 * switched to by adminStore.impersonateUser rather than an ordinary login — never hidden behind
 * a menu, so it's always obvious the app isn't showing the admin's own account.
 */
export const ImpersonationBanner = observer(() => {
  const userStore = useUserStore();

  const impersonatorToken: string = Storage.get(StorageProperties.ImpersonatorJwt);

  // Decoded only for the admin's name in the banner text — restoring the session on Exit re-uses
  // the raw token itself, not this decoded copy.
  const impersonator = useMemo(() => {
    if (!impersonatorToken) return undefined;
    return jwt.decode(impersonatorToken, { json: true })?.data as User | undefined;
  }, [impersonatorToken]);

  if (!impersonatorToken) return null;

  const exitImpersonation = () => {
    userStore.applySession(impersonatorToken, impersonator as User);
    Storage.remove(StorageProperties.ImpersonatorJwt);
    // Full reload, not navigate() — the impersonated user's data is still sitting in every other
    // store (projects, notifications, chat channels...); see ImpersonatePanel's startImpersonating
    // for the same reasoning in the other direction.
    window.location.href = '/admin';
  };

  return (
    <Box
      direction="row"
      align="center"
      justify="between"
      gap="small"
      background="#fdf1dc"
      border={{ side: 'bottom', color: '#f0d9a8' }}
      pad={{ vertical: '8px', horizontal: '16px' }}
      style={{ position: 'sticky', top: 0, zIndex: 3 }}
    >
      <Box gap="xxsmall">
        <Text size="14px" color="#7a4a00">
          <strong>
            You're viewing as {userStore.user?.first_name} {userStore.user?.last_name}
          </strong>
          {' · '}
          {userStore.user?.email}
          {' · '}
          {userStore.user?.active_group?.name || userStore.user?.groups?.[0]?.name || 'No account'}
          {' · '}
          {userStore.user?.is_admin
            ? 'Admin'
            : userStore.user?.performer_id
              ? 'Performer'
              : isTalentManagerUser(userStore.user)
                ? 'Talent manager'
                : 'Agency'}
        </Text>
        {impersonator && (
          <Text size="12px" color="#7a4a00">
            Admin: {impersonator.first_name} {impersonator.last_name} · {impersonator.email}
          </Text>
        )}
      </Box>
      <Button
        label="Back to admin"
        onClick={exitImpersonation}
        size="small"
        color="#222"
        style={{ background: 'white', border: '1px solid #e3cfa0', borderRadius: 18 }}
      />
    </Box>
  );
});
